import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 active:scale-95",
  {
    variants: {
      variant: {
        default:
          "bg-blue-600 text-white rounded-xl shadow-sm hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/20 active:shadow-none",
        destructive:
          "bg-red-500 text-white rounded-xl shadow-sm hover:bg-red-600 active:shadow-none",
        outline:
          "border-1.5 border-border bg-background rounded-xl hover:bg-muted hover:border-blue-300 dark:hover:border-blue-700 active:bg-muted/80",
        secondary:
          "bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80",
        ghost:
          "rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground",
        accent:
          "bg-emerald-500 text-white rounded-xl shadow-sm hover:bg-emerald-600 hover:shadow-md hover:shadow-emerald-500/20",
        link:
          "text-primary underline-offset-4 hover:underline p-0 h-auto shadow-none",
        premium:
          "bg-gradient-to-b from-blue-500 to-blue-700 text-white rounded-xl shadow-md shadow-blue-600/30 hover:shadow-lg hover:shadow-blue-600/30 hover:from-blue-400 hover:to-blue-600",
      },
      size: {
        default: "h-10 px-5 text-sm [&_svg]:size-4",
        sm:      "h-8 px-3.5 text-xs rounded-lg [&_svg]:size-3.5",
        lg:      "h-11 px-6 text-sm rounded-xl [&_svg]:size-4",
        xl:      "h-12 px-7 text-base rounded-2xl [&_svg]:size-5",
        icon:    "h-9 w-9 rounded-xl [&_svg]:size-4",
        "icon-sm": "h-7 w-7 rounded-lg [&_svg]:size-3.5",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
})
Button.displayName = "Button"

export { Button, buttonVariants }
